<script setup>
/* global axios */
import { onBeforeMount, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TdfAPI from 'dashboard/api/tdfConfig';

const { t } = useI18n();

const mapData = ref(null);
const isLoading = ref(false);
const selectedDate = ref(new Date().toISOString().slice(0, 10));
const filterText = ref('');
const filterType = ref('');
const activeStatusFilter = ref(null);
const activeGuestFilter = ref(null);
const activeHousekeepingFilter = ref(null);
const activeView = ref('map');

// Status colors
const roomStatusColors = {
  livre: { bg: 'bg-blue-500', text: 'text-white', dot: 'bg-blue-500' },
  ocupado: { bg: 'bg-orange-500', text: 'text-white', dot: 'bg-orange-500' },
  interditado: { bg: 'bg-slate-900', text: 'text-white', dot: 'bg-slate-900' },
};

const guestStatusConfig = {
  V: { label: 'TDF.MAPA.GUEST_STATUS.NO_CONFIRM', color: 'bg-rose-800' },
  N: { label: 'TDF.MAPA.GUEST_STATUS.TO_CONFIRM', color: 'bg-red-500' },
  P: { label: 'TDF.MAPA.GUEST_STATUS.CONFIRMED', color: 'bg-yellow-400' },
  F: { label: 'TDF.MAPA.GUEST_STATUS.BILLING', color: 'bg-blue-400' },
  T: { label: 'TDF.MAPA.GUEST_STATUS.PAID', color: 'bg-green-500' },
  B: { label: 'TDF.MAPA.GUEST_STATUS.PRE_BOOKING', color: 'bg-purple-500' },
};

const housekeepingConfig = {
  limpo: { label: 'TDF.MAPA.HOUSEKEEPING.CLEAN', color: 'bg-green-500' },
  limpando: { label: 'TDF.MAPA.HOUSEKEEPING.CLEANING', color: 'bg-green-400' },
  aguardar: { label: 'TDF.MAPA.HOUSEKEEPING.WAITING', color: 'bg-orange-400' },
  limpar: { label: 'TDF.MAPA.HOUSEKEEPING.TO_CLEAN', color: 'bg-red-600' },
};

const rooms = computed(() => mapData.value?.rooms || []);
const stats = computed(() => mapData.value?.stats || {});

const totalRooms = computed(() => stats.value.total || rooms.value.length);

const roomStatusStats = computed(() => {
  const total = totalRooms.value || 1;
  return {
    livre: {
      count:
        stats.value.livre ??
        rooms.value.filter(r => r.status === 'livre').length,
      pct: (
        ((stats.value.livre ??
          rooms.value.filter(r => r.status === 'livre').length) /
          total) *
        100
      ).toFixed(0),
    },
    ocupado: {
      count:
        stats.value.ocupado ??
        rooms.value.filter(r => r.status === 'ocupado').length,
      pct: (
        ((stats.value.ocupado ??
          rooms.value.filter(r => r.status === 'ocupado').length) /
          total) *
        100
      ).toFixed(0),
    },
    interditado: {
      count:
        stats.value.interditado ??
        rooms.value.filter(r => r.status === 'interditado').length,
      pct: (
        ((stats.value.interditado ??
          rooms.value.filter(r => r.status === 'interditado').length) /
          total) *
        100
      ).toFixed(0),
    },
  };
});

const guestStatusStats = computed(() => {
  const allGuests = rooms.value.flatMap(r => r.guests || []);
  const total = totalRooms.value || 1;
  const result = {};
  Object.keys(guestStatusConfig).forEach(key => {
    const count = allGuests.filter(g => g.payment_status === key).length;
    result[key] = { count, pct: ((count / total) * 100).toFixed(0) };
  });
  return result;
});

const housekeepingStats = computed(() => {
  const total = totalRooms.value || 1;
  const result = {};
  Object.keys(housekeepingConfig).forEach(key => {
    const count = rooms.value.filter(r => r.housekeeping === key).length;
    result[key] = { count, pct: ((count / total) * 100).toFixed(1) };
  });
  return result;
});

const filteredRooms = computed(() => {
  let result = rooms.value;

  if (activeStatusFilter.value) {
    result = result.filter(r => r.status === activeStatusFilter.value);
  }

  if (activeGuestFilter.value) {
    result = result.filter(r =>
      (r.guests || []).some(g => g.payment_status === activeGuestFilter.value)
    );
  }

  if (activeHousekeepingFilter.value) {
    result = result.filter(
      r => r.housekeeping === activeHousekeepingFilter.value
    );
  }

  if (filterText.value) {
    const search = filterText.value.toLowerCase();
    result = result.filter(
      r =>
        (r.name || '').toLowerCase().includes(search) ||
        (r.nro || '').toLowerCase().includes(search) ||
        (r.guests || []).some(g =>
          (g.name || '').toLowerCase().includes(search)
        )
    );
  }

  return result;
});

const enteringCount = computed(
  () =>
    stats.value.entrando ??
    rooms.value.filter(r => (r.guests || []).some(g => g.entering)).length
);

const occupiedGuestCount = computed(
  () =>
    rooms.value.filter(
      r => r.status === 'ocupado' && (r.guests || []).length > 0
    ).length
);

const fetchRoomMap = async () => {
  isLoading.value = true;
  try {
    const { data } = await TdfAPI.roomMap({ date: selectedDate.value });
    mapData.value = data;
  } catch {
    mapData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const changeDate = delta => {
  const d = new Date(selectedDate.value);
  d.setDate(d.getDate() + delta);
  selectedDate.value = d.toISOString().slice(0, 10);
  fetchRoomMap();
};

const toggleStatusFilter = status => {
  activeStatusFilter.value =
    activeStatusFilter.value === status ? null : status;
};

const toggleGuestFilter = status => {
  activeGuestFilter.value = activeGuestFilter.value === status ? null : status;
};

const toggleHousekeepingFilter = status => {
  activeHousekeepingFilter.value =
    activeHousekeepingFilter.value === status ? null : status;
};

const formatDateDisplay = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const getMainGuest = room => {
  const guests = room.guests || [];
  return guests.find(g => !g.entering) || guests[0] || null;
};

const getEnteringGuest = room => {
  return (room.guests || []).find(g => g.entering) || null;
};

onBeforeMount(() => {
  fetchRoomMap();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div
      class="flex items-center justify-between rounded-t-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3"
    >
      <span class="text-base font-bold uppercase tracking-wide text-white">
        {{ t('TDF.MAPA.TITLE') }}
      </span>
      <div class="flex items-center gap-2">
        <button
          v-for="view in ['map', 'housekeeping']"
          :key="view"
          class="rounded px-2 py-1 text-xs font-medium transition-colors"
          :class="
            activeView === view
              ? 'bg-white text-blue-600'
              : 'bg-blue-600 text-white/80 hover:text-white'
          "
          @click="activeView = view"
        >
          {{
            view === 'map'
              ? t('TDF.MAPA.VIEW_MAP')
              : t('TDF.MAPA.VIEW_HOUSEKEEPING')
          }}
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div
      class="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800"
    >
      <!-- Date -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('TDF.MAPA.FILTERS.START_DATE') }}
        </label>
        <div class="flex">
          <button
            class="rounded-l bg-blue-500 px-2 py-1.5 text-xs text-white hover:bg-blue-600"
            @click="changeDate(-1)"
          >
            &#9664;
          </button>
          <input
            v-model="selectedDate"
            type="date"
            class="border-y border-slate-200 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700"
            @change="fetchRoomMap"
          />
          <button
            class="rounded-r bg-blue-500 px-2 py-1.5 text-xs text-white hover:bg-blue-600"
            @click="changeDate(1)"
          >
            &#9654;
          </button>
        </div>
      </div>

      <!-- Type filter -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('TDF.MAPA.FILTERS.TYPE') }}
        </label>
        <select
          v-model="filterType"
          class="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700"
        >
          <option value="">
            {{ t('TDF.COMMON.ALL') }}
          </option>
        </select>
      </div>

      <!-- Text filter -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('TDF.MAPA.FILTERS.SEARCH') }}
        </label>
        <input
          v-model="filterText"
          type="text"
          :placeholder="t('TDF.COMMON.SEARCH')"
          class="rounded border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700"
        />
      </div>

      <div class="flex-1" />

      <!-- Action buttons -->
      <button
        class="rounded bg-orange-400 px-3 py-1.5 text-xs font-bold uppercase text-white hover:bg-orange-500"
        @click="fetchRoomMap"
      >
        {{ t('TDF.MAPA.FILTERS.FILTER') }}
      </button>
    </div>

    <!-- Status Chips -->
    <div
      class="flex flex-wrap gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
    >
      <!-- Room Status -->
      <div class="flex gap-0.5">
        <button
          v-for="(config, status) in roomStatusColors"
          :key="status"
          class="flex flex-col items-center gap-0.5 border px-3 py-1.5 text-[10px] transition-colors first:rounded-l-full last:rounded-r-full"
          :class="
            activeStatusFilter === status
              ? 'bg-blue-100 border-blue-400 dark:bg-blue-900'
              : 'bg-white border-slate-200 dark:bg-slate-700 dark:border-slate-600'
          "
          @click="toggleStatusFilter(status)"
        >
          <span
            class="inline-block size-2.5 rounded-full"
            :class="config.dot"
          />
          <span class="font-bold uppercase">
            {{ t(`TDF.MAPA.ROOM_STATUS.${status.toUpperCase()}`) }}
          </span>
          <small class="text-slate-500">
            {{ roomStatusStats[status]?.count || 0 }}
            ({{ roomStatusStats[status]?.pct || 0 }}%)
          </small>
        </button>
      </div>

      <!-- Guest Status -->
      <div class="flex gap-0.5">
        <button
          v-for="(config, code) in guestStatusConfig"
          :key="code"
          class="flex flex-col items-center gap-0.5 border px-2.5 py-1.5 text-[10px] transition-colors first:rounded-l-full last:rounded-r-full"
          :class="
            activeGuestFilter === code
              ? 'bg-blue-100 border-blue-400 dark:bg-blue-900'
              : 'bg-white border-slate-200 dark:bg-slate-700 dark:border-slate-600'
          "
          @click="toggleGuestFilter(code)"
        >
          <span
            class="inline-block size-2.5 rounded-full"
            :class="config.color"
          />
          <span class="font-bold">
            {{ t(config.label) }}
          </span>
          <small class="text-slate-500">
            {{ guestStatusStats[code]?.count || 0 }}
            ({{ guestStatusStats[code]?.pct || 0 }}%)
          </small>
        </button>
      </div>

      <!-- Housekeeping Status -->
      <div class="flex gap-0.5">
        <button
          v-for="(config, status) in housekeepingConfig"
          :key="status"
          class="flex flex-col items-center gap-0.5 border px-2.5 py-1.5 text-[10px] transition-colors first:rounded-l-full last:rounded-r-full"
          :class="
            activeHousekeepingFilter === status
              ? 'bg-blue-100 border-blue-400 dark:bg-blue-900'
              : 'bg-white border-slate-200 dark:bg-slate-700 dark:border-slate-600'
          "
          @click="toggleHousekeepingFilter(status)"
        >
          <span
            class="inline-block size-2.5 rounded-full"
            :class="config.color"
          />
          <span class="font-bold">
            {{ t(config.label) }}
          </span>
          <small class="text-slate-500">
            {{ housekeepingStats[status]?.count || 0 }}
            ({{ housekeepingStats[status]?.pct || 0 }}%)
          </small>
        </button>
      </div>
    </div>

    <!-- Sub-filter row (In/Out counts) -->
    <div
      class="flex items-center gap-4 rounded-lg border border-slate-200 bg-blue-50/50 px-3 py-2 text-xs dark:border-slate-600 dark:bg-slate-800"
    >
      <div class="flex items-center gap-2">
        <span class="font-bold text-blue-600">In</span>
        <span class="text-slate-600 dark:text-slate-300">
          {{ t('TDF.MAPA.ENTERING') }}: {{ enteringCount }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="font-bold text-orange-600">Out</span>
        <span class="text-slate-600 dark:text-slate-300">
          {{ t('TDF.MAPA.OCCUPIED') }}: {{ occupiedGuestCount }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-slate-600 dark:text-slate-300">
          {{ t('TDF.MAPA.EXITS') }}: {{ stats.saidas || 0 }}
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="text-slate-500">{{ t('TDF.COMMON.LOADING') }}</span>
    </div>

    <!-- Room Map Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-xs" style="table-layout: fixed">
        <thead>
          <tr class="bg-slate-100 dark:bg-slate-700">
            <th
              class="w-1/4 px-2 py-2 text-left text-[10px] font-bold uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.MAPA.COL_UNIT') }}
            </th>
            <th
              class="w-1/4 px-2 py-2 text-left text-[10px] font-bold uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.MAPA.COL_ENTERING') }}
            </th>
            <th
              class="w-1/4 px-2 py-2 text-left text-[10px] font-bold uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.MAPA.COL_OCCUPIED') }}
            </th>
            <th
              class="w-1/4 px-2 py-2 text-left text-[10px] font-bold uppercase text-slate-500 dark:text-slate-300"
            >
              {{ t('TDF.MAPA.COL_HOUSEKEEPING') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(room, idx) in filteredRooms"
            :key="room.cod"
            class="border-b border-slate-100 transition-colors dark:border-slate-700"
            :class="
              idx % 2 === 0
                ? 'bg-green-50/60 dark:bg-green-900/10'
                : 'bg-green-50/80 dark:bg-green-900/15'
            "
          >
            <!-- Column 1: Room Unit -->
            <td class="px-2 py-1.5 align-middle">
              <div class="flex items-center gap-2">
                <!-- Room status dot -->
                <span
                  class="inline-block size-3 shrink-0 rounded-full"
                  :class="roomStatusColors[room.status]?.dot || 'bg-slate-400'"
                />
                <div class="min-w-0 flex-1">
                  <div
                    class="truncate text-xs font-bold text-blue-600 dark:text-blue-400"
                  >
                    {{ room.nro }} - {{ room.name }}
                  </div>
                  <div
                    v-if="room.status === 'interditado' && room.interdiction"
                    class="truncate text-[10px] text-slate-500"
                  >
                    {{
                      typeof room.interdiction === 'object'
                        ? room.interdiction.motivo
                        : room.interdiction
                    }}
                  </div>
                  <div
                    v-else-if="room.status === 'livre'"
                    class="text-[10px] text-slate-400"
                  >
                    {{ t('TDF.MAPA.AVAILABLE') }}
                  </div>
                </div>
                <!-- Source badge -->
                <span
                  v-if="room.source === 'motor_reserva'"
                  class="shrink-0 rounded bg-blue-100 px-1 py-0.5 text-[8px] font-bold uppercase text-blue-600"
                >
                  MR
                </span>
              </div>
            </td>

            <!-- Column 2: Entering Guest -->
            <td class="px-2 py-1.5 align-middle">
              <div
                v-if="getEnteringGuest(room)"
                class="flex items-center gap-2"
              >
                <span
                  class="inline-block size-2.5 shrink-0 rounded-full"
                  :class="
                    guestStatusConfig[getEnteringGuest(room).payment_status]
                      ?.color || 'bg-slate-300'
                  "
                />
                <div class="min-w-0 flex-1">
                  <div
                    class="truncate text-xs font-bold text-blue-600 dark:text-blue-400"
                  >
                    {{ getEnteringGuest(room).name || '-' }}
                  </div>
                  <div class="text-[10px] text-slate-500">
                    {{ formatDateDisplay(getEnteringGuest(room).check_in) }} -
                    {{ formatDateDisplay(getEnteringGuest(room).check_out) }}
                  </div>
                </div>
              </div>
            </td>

            <!-- Column 3: Occupied Guest -->
            <td class="px-2 py-1.5 align-middle">
              <div
                v-if="getMainGuest(room) && room.status === 'ocupado'"
                class="relative flex items-center gap-2"
              >
                <span
                  class="inline-block size-2.5 shrink-0 rounded-full"
                  :class="
                    guestStatusConfig[getMainGuest(room).payment_status]
                      ?.color || 'bg-slate-300'
                  "
                />
                <div class="min-w-0 flex-1">
                  <div
                    class="truncate text-xs font-bold text-blue-600 dark:text-blue-400"
                  >
                    {{ getMainGuest(room).name || '-' }}
                  </div>
                  <div
                    v-if="getMainGuest(room).companion"
                    class="truncate text-[10px] text-blue-500"
                  >
                    {{ getMainGuest(room).companion }}
                  </div>
                  <div class="text-[10px] text-slate-500">
                    {{ formatDateDisplay(getMainGuest(room).check_in) }} -
                    {{ formatDateDisplay(getMainGuest(room).check_out) }}
                  </div>
                </div>
                <!-- Guest count badge -->
                <span
                  v-if="room.guest_count > 0"
                  class="absolute right-0 top-0 flex size-4 items-center justify-center rounded bg-blue-500/20 text-[10px] font-bold text-blue-600"
                >
                  {{ room.guest_count }}
                </span>
              </div>
            </td>

            <!-- Column 4: Housekeeping -->
            <td class="px-2 py-1.5 align-middle">
              <div class="flex items-center gap-2">
                <span
                  class="inline-block size-2.5 shrink-0 rounded-full"
                  :class="
                    housekeepingConfig[room.housekeeping]?.color ||
                    'bg-slate-300'
                  "
                />
                <div class="min-w-0">
                  <div
                    class="text-xs font-bold uppercase text-blue-600 dark:text-blue-400"
                  >
                    {{ room.housekeeping || 'limpar' }}
                  </div>
                  <div
                    v-if="getMainGuest(room)"
                    class="text-[10px] text-slate-500"
                  >
                    {{ formatDateDisplay(getMainGuest(room)?.check_in) }} -
                    {{ formatDateDisplay(getMainGuest(room)?.check_out) }}
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <!-- Totals Row -->
          <tr
            v-if="filteredRooms.length > 0"
            class="border-t-2 border-slate-300 bg-slate-200/60 font-bold dark:border-slate-500 dark:bg-slate-700/60"
          >
            <td class="px-2 py-2 text-xs text-slate-700 dark:text-slate-200">
              {{ totalRooms }} {{ t('TDF.MAPA.ROOMS_TOTAL') }}
            </td>
            <td
              class="px-2 py-2 text-right text-xs text-slate-600 dark:text-slate-300"
            >
              {{ enteringCount }}
            </td>
            <td
              class="px-2 py-2 text-right text-xs text-slate-600 dark:text-slate-300"
            >
              {{ occupiedGuestCount }}
            </td>
            <td class="px-2 py-2" />
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <p
      v-if="!isLoading && filteredRooms.length === 0"
      class="py-8 text-center text-sm text-slate-500"
    >
      {{ t('TDF.MAPA.EMPTY') }}
    </p>
  </div>
</template>
